import axios from 'axios';
import { toast } from 'sonner';
import { auth } from './firebase';
import { getAuthBearerToken, resetAuthTokenState } from './authToken';
import { logStructured } from './networkLog';

function isSoluQrProductionHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'soluqr.cl' || h === 'www.soluqr.cl';
}

/** Resuelve en runtime (navegador). No usar const top-level: en build CRA no hay window. */
export function resolveApiBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = (window.location.hostname || '').toLowerCase();
    // Producción SoluQR: mismo origen → Vercel proxy /api → Railway (sin CORS).
    if (isSoluQrProductionHost(host)) {
      return `${window.location.origin.replace(/\/$/, '')}/api`;
    }
    if (host === 'localhost' || host === '127.0.0.1') {
      const fromEnv = String(process.env.REACT_APP_BACKEND_URL || '').trim();
      const base = fromEnv ? fromEnv.replace(/\/$/, '') : 'http://localhost:8000';
      return `${base}/api`;
    }
    return `${window.location.origin.replace(/\/$/, '')}/api`;
  }
  // Build estático (Node): relativo — en soluqr.cl el bundle corre en el browser y re-evalúa arriba.
  const fromEnv = String(process.env.REACT_APP_BACKEND_URL || '').trim();
  if (fromEnv) return `${fromEnv.replace(/\/$/, '')}/api`;
  return '/api';
}

export function getApiBaseUrl() {
  return resolveApiBaseUrl();
}

/**
 * Base URL opcional del microservicio SoluGo (Fase 5).
 * En soluqr.cl usa el proxy same-origin (/api/solugo → Vercel) para evitar CORS.
 * REACT_APP_SOLUGO_API_URL aplica en local u otros hosts (staging directo).
 */
export function resolveSolugoApiBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = (window.location.hostname || '').toLowerCase();
    if (isSoluQrProductionHost(host)) {
      return null;
    }
  }
  const fromEnv = String(process.env.REACT_APP_SOLUGO_API_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  return null;
}

/**
 * Base URL opcional del microservicio Core (Fase 5 / ítem 27).
 * Menú, pedidos, SaaS, customers, inventario — todo excepto assistant/analytics/solugo/club.
 */
export function resolveCoreApiBaseUrl() {
  const fromEnv = String(process.env.REACT_APP_CORE_API_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  return null;
}

function _isSolugoApiPath(url) {
  const path = String(url || '').split('?')[0];
  return path.startsWith('/solugo') || path.startsWith('solugo/');
}

/** Rutas que siguen en el monolito tras cutover Core (assistant, analytics, club Pro). */
function _isMonolithOnlyApiPath(url) {
  const path = String(url || '').split('?')[0];
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (_isSolugoApiPath(normalized)) return true;
  const monolithOnly = ['/assistant', '/analytics', '/broadcast'];
  return monolithOnly.some((p) => normalized.startsWith(p));
}

function _isCoreApiPath(url) {
  const path = String(url || '').split('?')[0];
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (_isSolugoApiPath(normalized)) return false;
  return !_isMonolithOnlyApiPath(normalized);
}

/** @deprecated usar getApiBaseUrl() en runtime */
export const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Rutas públicas de cliente: no redirigir a /login si el token caduca. */
function isPublicCustomerPath() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname || '';
  return (
    p.startsWith('/menu/') ||
    p === '/catalog' ||
    p === '/checkout' ||
    p === '/assistant' ||
    p === '/profile' ||
    p.startsWith('/order-status/') ||
    p === '/register' ||
    p === '/accept-invite' ||
    p === '/solugo' ||
    p.startsWith('/solugo/rider') ||
    p.startsWith('/solugo/track') ||
    p.startsWith('/solugo/local') ||
    p === '/colina' ||
    p.startsWith('/colina/') ||
    p.startsWith('/own-delivery') ||
    p.startsWith('/debug-api')
  );
}

// Request interceptor — token cacheado; refresh forzado solo en retry auth
api.interceptors.request.use(
  async (config) => {
    const solugoBase = resolveSolugoApiBaseUrl();
    const coreBase = resolveCoreApiBaseUrl();
    const useSolugoService = solugoBase && _isSolugoApiPath(config.url);
    const useCoreService = coreBase && _isCoreApiPath(config.url);
    config.baseURL = useSolugoService
      ? solugoBase
      : useCoreService
        ? coreBase
        : resolveApiBaseUrl();
    if (typeof window !== 'undefined' && !window.__SOLUQR_API_BASE_LOGGED__) {
      window.__SOLUQR_API_BASE_LOGGED__ = true;
      // eslint-disable-next-line no-console
      console.info('[soluqr] API base:', resolveApiBaseUrl());
      if (solugoBase) {
        // eslint-disable-next-line no-console
        console.info('[soluqr] SoluGo API base:', solugoBase);
      }
      if (coreBase) {
        // eslint-disable-next-line no-console
        console.info('[soluqr] Core API base:', coreBase);
      }
    }
    if (config.skipAuth) {
      delete config.headers.Authorization;
      return config;
    }
    const token = await getAuthBearerToken(false);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (!originalRequest) {
      logStructured('network', {
        event: 'error_no_config',
        message: error.message,
        code: error.code,
      });
      return Promise.reject(error);
    }

    if (!error.response) {
      logStructured('network', {
        event: 'error_no_response',
        message: error.message,
        code: error.code,
        url: originalRequest.url,
        method: originalRequest.method,
      });
      return Promise.reject(error);
    }

    if (status >= 500 && !originalRequest._silentServerError) {
      logStructured('network', {
        event: 'server_error',
        status,
        url: originalRequest.url,
        method: originalRequest.method,
      });
    }

    if (status === 403 && !originalRequest._forbiddenHandled) {
      originalRequest._forbiddenHandled = true;
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : 'No tenés permiso para esta acción.';
      // HTTPBearer sin sesión SoluQR: ruido típico en panel local/rider (usan otro token).
      const isBearerNoise =
        typeof detail === 'string' &&
        /^not authenticated$/i.test(detail.trim());
      const silentBearerOnPublic =
        isBearerNoise && isPublicCustomerPath();
      if (!originalRequest._silentForbidden && !silentBearerOnPublic) {
        toast.error(message);
      }
      logStructured('auth', {
        event: 'forbidden_stay_on_page',
        status,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        silent: Boolean(originalRequest._silentForbidden || silentBearerOnPublic),
      });
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._anonymousRetry) {
      if (!originalRequest._authRetryDone) {
        originalRequest._authRetryDone = true;
        try {
          resetAuthTokenState();
          await auth.authStateReady();
          const token = await getAuthBearerToken(true);
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          logStructured('auth', {
            event: 'response_token_refresh_failed',
            status,
            message: refreshError?.message,
            publicPath: isPublicCustomerPath(),
          });
        }
      }

      if (auth.currentUser) {
        logStructured('auth', {
          event: '401_with_firebase_session_no_redirect',
          status,
          path: typeof window !== 'undefined' ? window.location.pathname : '',
        });
        return Promise.reject(error);
      }

      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];

      if (isPublicCustomerPath()) {
        originalRequest._anonymousRetry = true;
        delete originalRequest.headers.Authorization;
        originalRequest.skipAuth = true;
        logStructured('auth', {
          event: 'continuing_without_auth_public_route',
          status,
          path: typeof window !== 'undefined' ? window.location.pathname : '',
        });
        return api(originalRequest);
      }

      logStructured('auth', {
        event: 'redirect_login',
        status,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  }
};

export default api;